import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  LayoutGrid,
  MapPin,
  Building2,
  Hash,
  RefreshCw,
  VideoOff,
} from "lucide-react";
import { Header } from "../components/Header";
import { VideoPlayer } from "../components/VideoPlayer";
import { camerasApi } from "../api";
import { Camera, demoCameras } from "../types";
import styles from "./CameraView.module.css";

type LoadingState = "idle" | "loading" | "loaded" | "error";

const installationTypeLabels: Record<Camera["installationType"], string> = {
  roof: "Крыша",
  facade: "Фасад",
  cabin: "Бокс",
};

export function CameraViewPage() {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const [camera, setCamera] = useState<Camera | null>(null);
  const [loadingState, setLoadingState] = useState<LoadingState>("idle");
  const [error, setError] = useState<string>("");
  const [isDemoMode, setIsDemoMode] = useState(false);

  useEffect(() => {
    if (documentId) {
      fetchCamera();
    }
  }, [documentId]);

  const fetchCamera = async () => {
    console.log(documentId);
    if (!documentId) return;

    setLoadingState("loading");
    setError("");
    setIsDemoMode(false);

    try {
      // API уже возвращает плоские данные
      const data = await camerasApi.getOne(String(documentId));
      setCamera(data);
      setLoadingState("loaded");
    } catch (err: any) {
      // Check for demo camera data
      const demoCamera = demoCameras.find(
        (c) => c.documentId === String(documentId),
      );

      if (demoCamera) {
        setCamera(demoCamera);
        setLoadingState("loaded");
        setIsDemoMode(true);
        setError("Демо-режим: используются тестовые данные");
      } else if (err.response?.status === 403) {
        setError("Доступ запрещён");
        setLoadingState("error");
      } else if (err.response?.status === 404) {
        setError("Камера не найдена");
        setLoadingState("error");
      } else {
        setError("Ошибка загрузки данных");
        setLoadingState("error");
      }
    }
  };

  const handleRetry = () => {
    fetchCamera();
  };

  const handlePlayerError = (err: Error) => {
    console.error("Video player error:", err);
  };

  const handleBack = () => {
    navigate("/cameras");
  };

  const handleGridClick = () => {
    navigate("/grid");
  };

  const handleLogout = () => {
    navigate("/login");
  };

  return (
    <div className={styles.page}>
      <Header onNavigate={handleGridClick} onLogout={handleLogout} />

      <main className={styles.main}>
        <button className={styles.backLink} onClick={handleBack}>
          <ArrowLeft size={18} />
          Назад к списку
        </button>

        <div className={styles.content}>
          <div className={styles.playerSection}>
            <div className={styles.playerWrapper}>
              {loadingState === "loading" ? (
                <div className={styles.loadingCard}>
                  <div className={styles.loadingSpinner} />
                  <span className={styles.loadingText}>
                    Загрузка данных камеры...
                  </span>
                </div>
              ) : loadingState === "error" ? (
                <div className={styles.errorCard}>
                  <VideoOff size={48} className={styles.errorIcon} />
                  <h3 className={styles.errorTitle}>Поток недоступен</h3>
                  <p className={styles.errorText}>{error}</p>
                  <button className={styles.retryButton} onClick={handleRetry}>
                    <RefreshCw size={16} />
                    Попробовать снова
                  </button>
                </div>
              ) : camera ? (
                <>
                  {isDemoMode && (
                    <div className={styles.demoBanner}>Демо-режим</div>
                  )}
                  <VideoPlayer
                    streamUrl={camera.streamUrl || undefined}
                    autoPlay
                    showStatus
                    status={camera.cameraStatus}
                    onError={handlePlayerError}
                  />
                </>
              ) : null}
            </div>

            <div className={styles.actionsRow}>
              <button className={styles.actionButton} onClick={handleBack}>
                <ArrowLeft size={18} />К списку
              </button>
              <button
                className={`${styles.actionButton} ${styles.actionButtonPrimary}`}
                onClick={handleGridClick}
              >
                <LayoutGrid size={18} />
                Открыть в сетке
              </button>
            </div>
          </div>

          <aside className={styles.sidebar}>
            {camera && (
              <div className={styles.infoCard}>
                <div className={styles.infoHeader}>
                  <h1 className={styles.cameraName}>{camera.name}</h1>
                  <div
                    className={`${styles.statusBadge} ${
                      camera.cameraStatus === "active"
                        ? styles.statusActive
                        : styles.statusInactive
                    }`}
                  >
                    <span className={styles.statusDot} />
                    {camera.cameraStatus === "active" ? "Онлайн" : "Офлайн"}
                  </div>
                </div>

                <div className={styles.infoList}>
                  <div className={styles.infoItem}>
                    <MapPin size={18} className={styles.infoIcon} />
                    <div className={styles.infoContent}>
                      <span className={styles.infoLabel}>Адрес</span>
                      <span className={styles.infoValue}>{camera.address}</span>
                    </div>
                  </div>

                  <div className={styles.infoItem}>
                    <Building2 size={18} className={styles.infoIcon} />
                    <div className={styles.infoContent}>
                      <span className={styles.infoLabel}>Объект</span>
                      <span className={styles.infoValue}>
                        {camera.objectName}
                      </span>
                    </div>
                  </div>

                  <div className={styles.infoItem}>
                    <Hash size={18} className={styles.infoIcon} />
                    <div className={styles.infoContent}>
                      <span className={styles.infoLabel}>Тип установки</span>
                      <span className={styles.infoValue}>
                        {installationTypeLabels[camera.installationType]}
                      </span>
                    </div>
                  </div>

                  <div className={styles.infoItem}>
                    <Hash size={18} className={styles.infoIcon} />
                    <div className={styles.infoContent}>
                      <span className={styles.infoLabel}>ID камеры</span>
                      <span className={styles.infoValue}>{camera.id}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </aside>
        </div>
      </main>
    </div>
  );
}
